import acceptLanguage from 'accept-language';
import {
  GetServerSidePropsContext,
  GetServerSidePropsResult,
  NextPage,
} from 'next';
import { IBusinessUnit } from '../utils/businessUnitMappingHelpers/businessUnitMappingHelpers';
import { getToken } from 'next-auth/jwt';

export interface IUser {
  name: string;
  title: string;
}

export const userSupportedLanguages = [
  'en',
  'es',
  'fr',
  'it',
  'pt',
  'pt-BR',
  'de',
  'es-MX',
  'fr-CA',
  'pl',
  'ru',
  'tr',
  'ko',
  'vi',
  'ja',
];
acceptLanguage.languages(userSupportedLanguages);

export type SecurePage<P = Record<string, unknown>> = NextPage<
  P & {
    user: IUser;
    businessUnit: string;
    username: string;
    locale: string;
  }
>;

export const withUser =
  <Out>(
    fn?: (ctx: GetServerSidePropsContext) => GetServerSidePropsResult<Out>,
  ) =>
  async (ctx: GetServerSidePropsContext) => {
    const [composedProps] = await Promise.all([
      fn ? fn(ctx) : Promise.resolve(),
    ]);
    const { query, req } = ctx;
    const session = await getToken({ req });
    const businessUnit = query?.businessUnit as IBusinessUnit['type'];
    const queryLocale = query.locale ? String(query.locale) : null;
    const lang =
      acceptLanguage.get(queryLocale || req?.headers['accept-language']) ||
      'en';
    if (
      session &&
      session?.businessUnit !== businessUnit &&
      businessUnit !== 'tps'
    ) {
      return {
        redirect: {
          destination: `/error/404`,
        },
      };
    }
    return {
      ...composedProps,
      props: {
        lang: lang,
        locale: lang,
        businessUnit,
        username: session?.username || '',
      },
    };
  };

export const withLang =
  <Out>(
    fn?: (ctx: GetServerSidePropsContext) => GetServerSidePropsResult<Out>,
  ) =>
  async (ctx: GetServerSidePropsContext) => {
    const composedProps = await (fn ? fn(ctx) : Promise.resolve({}));

    const { query, req } = ctx;

    const queryLocale = query.locale ? String(query.locale) : null;
    const lang =
      acceptLanguage.get(queryLocale || req?.headers['accept-language']) ||
      'en';

    return {
      ...composedProps,
      props: {
        ...('props' in composedProps ? composedProps.props : {}),
        lang,
      },
    };
  };
